import React, { useCallback, useMemo } from 'react';
import partial from 'lodash-es/partial';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import { FilterSelectionType } from 'shared/types/graphql';
import { Box, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Button } from '@televet/kibble-ui/build/components/Button';
import {
  ChannelListFilterDraft,
  ChannelListFilterDraftValue,
  ChannelListFilterDraftValueOption,
} from '../types/ChannelListFilterDraft';
import ChannelListFilterRow from './ChannelListFilterRow';
import { ChannelListFilterSelectionDraft } from '../types/ChannelListFilterSelectionDraft';
import { IChannelListFilterSelection } from 'pages/Conversations/interfaces/IChannelListFilterSelection';
import { convertDraftFilterToPersisted } from '../utils/convertDraftFilterToPersisted';
import { defaultChannelListFilter, IChannelListFilter } from 'pages/Conversations/interfaces/IChannelListFilter';
import useChannelFilterOptions from '../../../../hooks/useChannelFilterOptions';
import { IFilterOption } from '../interfaces/IFilterOption';
import ChannelListFilterPopoverHeader from './ChannelListFilterPopoverHeader';
import { ChannelListFilterRowLabel } from '../types/ChannelListFilterRowLabel';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IChannelListFilterEditProps {
  currentChannelFilterSelection: IChannelListFilterSelection;
  onFilterApply: (channelFilterSelection: IChannelListFilterSelection) => void;
  onSavedFiltersClick: () => void;
  onClearFilterClick: (source: string) => void;
  onSaveNewFilterClick: () => void;
  onClosePopover: () => void;
  onShowArchivedToggle: () => void;
}

const ChannelListFilterEdit = ({
  currentChannelFilterSelection,
  onFilterApply,
  onSavedFiltersClick,
  onClearFilterClick,
  onSaveNewFilterClick,
  onClosePopover,
  onShowArchivedToggle,
}: IChannelListFilterEditProps): JSX.Element => {
  const {
    channelStatusOptions,
    clinicUserOptions,
    clinicTagOptions,
    clinicEmployeeOptions,
    appointmentTypeOptions,
    clinicRoomOptions,
  } = useChannelFilterOptions();
  const { trackEvent } = useTracking();

  const getSelectedOptions = useCallback(
    (key: keyof IChannelListFilter, filter: IChannelListFilter, options: IFilterOption[]) => {
      const selectedIds = filter[key];
      if (typeof selectedIds === 'boolean') {
        return [] as ChannelListFilterDraftValueOption[];
      }
      return selectedIds
        .map((id) => {
          const option = options.find(({ value }) => value === id);
          if (!option) return;
          return option;
        })
        .filter((v) => !!v) as ChannelListFilterDraftValueOption[];
    },
    [],
  );

  const onSetFilter = useCallback(
    (filters: ChannelListFilterDraft) => {
      const appliedFilter = convertDraftFilterToPersisted({
        id: '',
        name: '',
        isDeleted: false,
        filterSelectionType: FilterSelectionType.Private,
        filters,
      });
      onFilterApply(appliedFilter);
    },
    [onFilterApply],
  );

  const selectedChannelStatusOptions = useMemo(
    () =>
      channelStatusOptions.reduce((prev, curr) => {
        prev.push(
          ...getSelectedOptions(
            'channelStatusIds',
            currentChannelFilterSelection.filters,
            curr.value as ChannelListFilterDraftValueOption[],
          ),
        );
        return prev;
      }, [] as ChannelListFilterDraftValueOption[]),
    [currentChannelFilterSelection, channelStatusOptions, getSelectedOptions],
  );
  const selectedClinicUserOptions = useMemo(
    () =>
      clinicUserOptions.reduce((prev, curr) => {
        prev.push(
          ...getSelectedOptions(
            'assigneeIds',
            currentChannelFilterSelection.filters,
            curr.value as ChannelListFilterDraftValueOption[],
          ),
        );
        return prev;
      }, [] as ChannelListFilterDraftValueOption[]),
    [currentChannelFilterSelection, clinicUserOptions, getSelectedOptions],
  );
  const selectedChannelTagOptions = useMemo(
    () => getSelectedOptions('tagNames', currentChannelFilterSelection.filters, clinicTagOptions),
    [currentChannelFilterSelection, clinicTagOptions, getSelectedOptions],
  );
  const selectedClinicEmployeeOptions = useMemo(
    () =>
      getSelectedOptions('appointmentClinicEmployeeIds', currentChannelFilterSelection.filters, clinicEmployeeOptions),
    [currentChannelFilterSelection, clinicEmployeeOptions, getSelectedOptions],
  );
  const selectedAppointmentTypeOptions = useMemo(
    () => getSelectedOptions('appointmentTypeIds', currentChannelFilterSelection.filters, appointmentTypeOptions),
    [currentChannelFilterSelection, appointmentTypeOptions, getSelectedOptions],
  );
  const selectedClinicRoomOptions = useMemo(
    () => getSelectedOptions('appointmentRoomIds', currentChannelFilterSelection.filters, clinicRoomOptions),
    [currentChannelFilterSelection, clinicRoomOptions, getSelectedOptions],
  );

  const filterDraft: ChannelListFilterSelectionDraft = useMemo(() => {
    const {
      id,
      name,
      isDeleted,
      filterSelectionType,
      filters: { showArchived },
    } = currentChannelFilterSelection;
    return {
      id,
      name,
      isDeleted,
      filterSelectionType,
      filters: {
        channelStatusIds: selectedChannelStatusOptions,
        assigneeIds: selectedClinicUserOptions,
        tagNames: selectedChannelTagOptions,
        appointmentClinicEmployeeIds: selectedClinicEmployeeOptions,
        appointmentTypeIds: selectedAppointmentTypeOptions,
        appointmentRoomIds: selectedClinicRoomOptions,
        unread: [],
        showArchived,
      },
    };
  }, [
    currentChannelFilterSelection,
    selectedChannelStatusOptions,
    selectedClinicUserOptions,
    selectedChannelTagOptions,
    selectedClinicEmployeeOptions,
    selectedAppointmentTypeOptions,
    selectedClinicRoomOptions,
  ]);

  const isPopoverFilterApplied = useMemo(
    () => !isEqual(omit(currentChannelFilterSelection.filters, 'unread'), omit(defaultChannelListFilter, 'unread')),
    [currentChannelFilterSelection.filters],
  );

  const handleValueChange = useCallback(
    (key: keyof IChannelListFilter, newValue: ChannelListFilterDraftValue): void => {
      const newFilter = {
        ...filterDraft.filters,
        [key]: newValue,
      };
      onSetFilter(newFilter);
    },
    [onSetFilter, filterDraft.filters],
  );

  const handleSavedFiltersClicked = (): void => {
    onSavedFiltersClick();
  };

  const showArchived = currentChannelFilterSelection.filters.showArchived;
  const handleShowArchivedToggle = useCallback(
    (source: string): void => {
      trackEvent(ConversationEvents.FilterShowArchivedToggled, {
        value: !showArchived,
        source,
      });
      onShowArchivedToggle();
    },
    [trackEvent, showArchived, onShowArchivedToggle],
  );

  return (
    <Flex data-testid="channel-list-filter-popover-content" direction="column" overflow="hidden">
      <Box flex="0 0 auto" borderBottom="1px" borderBottomColor="border.default">
        <Flex align="center" justify="space-between" p={3} pl={4}>
          <Heading size="md" fontWeight="bold">
            Filters
          </Heading>
          <HStack>
            <ChannelListFilterPopoverHeader
              iconFirst={false}
              onHandleSaveFilterButtonClick={handleSavedFiltersClicked}
              isClearButtonDisabled={!isPopoverFilterApplied}
              onClearButtonClick={partial(onClearFilterClick, 'edit-view-header-clear-button')}
            />
          </HStack>
        </Flex>
        {!!currentChannelFilterSelection.id && (
          <Flex
            bg="background.primaryAlpha"
            borderTopStyle="solid"
            borderTopWidth="1px"
            borderTopColor="border.default"
            py="3"
            px="4"
          >
            <Text size="sm" fontWeight="bold" mr="2">
              Saved Filter:
            </Text>
            <Tag
              label={currentChannelFilterSelection.name}
              bg="background.contrast"
              color="text.onContrast"
              onClose={partial(onClearFilterClick, 'edit-view-saved-filter-tag-close-button')}
              closeButtonDisplay="always"
            />
          </Flex>
        )}
      </Box>
      <Box data-testid="channel-list-popover-body-filter-edit" flex="1 1 auto" overflow="auto">
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationStatus}
          selectedOptions={selectedChannelStatusOptions}
          options={channelStatusOptions}
          showArchived={showArchived}
          isReadOnly={false}
          onShowArchivedToggle={partial(handleShowArchivedToggle, 'conversations edit filter popover status select')}
          onValueChange={partial(handleValueChange, 'channelStatusIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationAssignees}
          selectedOptions={selectedClinicUserOptions}
          options={clinicUserOptions}
          isReadOnly={false}
          showSelectAllOption={false}
          onValueChange={partial(handleValueChange, 'assigneeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationTags}
          selectedOptions={selectedChannelTagOptions}
          options={clinicTagOptions}
          isReadOnly={false}
          onValueChange={partial(handleValueChange, 'tagNames')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentProviders}
          selectedOptions={selectedClinicEmployeeOptions}
          options={clinicEmployeeOptions}
          isReadOnly={false}
          onValueChange={partial(handleValueChange, 'appointmentClinicEmployeeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentTypes}
          selectedOptions={selectedAppointmentTypeOptions}
          options={appointmentTypeOptions}
          isReadOnly={false}
          onValueChange={partial(handleValueChange, 'appointmentTypeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentRooms}
          selectedOptions={selectedClinicRoomOptions}
          options={clinicRoomOptions}
          isReadOnly={false}
          isLastRow={true}
          onValueChange={partial(handleValueChange, 'appointmentRoomIds')}
        />
      </Box>
      <Box borderTop="1px" borderTopColor="border.default" flex="0 0 auto" px={4} py={3}>
        <Flex align="center" justify="space-between">
          <Box>
            <HStack as="label" cursor="pointer">
              <Box>
                <Switch
                  isFieldInline
                  isChecked={showArchived}
                  onChange={partial(handleShowArchivedToggle, 'conversations edit filter popover footer')}
                />
              </Box>
              <Text size="sm">Show Archived</Text>
            </HStack>
          </Box>
          <HStack>
            <Button
              data-testid={`channel-list-filter-popover-footer-Save-New-Filter`}
              variant="secondary"
              size="sm"
              onClick={onSaveNewFilterClick}
            >
              Save New Filter
            </Button>
            <Button data-testid="channel-list-filter-popover-footer-Done" size="sm" onClick={onClosePopover}>
              Done
            </Button>
          </HStack>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ChannelListFilterEdit;
